import { type MotionValue, useMotionTemplate, useTransform } from 'motion/react'

export interface UseScrollFadeMaskImageProps {
  /**
   * Scroll Y start value from {@link useScrollOffsets}.
   */
  scrollYStart: MotionValue<number>

  /**
   * Scroll Y end value from {@link useScrollOffsets}.
   */
  scrollYEnd: MotionValue<number>

  /**
   * Where to apply the scroll fade effect.
   *
   * @default 'both'
   */
  fade?: 'top' | 'bottom' | 'both' | 'none'

  /**
   * How tall the underlying gradient segment should be.
   * Can be a `number` which represents a pixel value, or a string which
   * will be interpreted as a CSS value.
   *
   * @default 'var(--spacing-4xl)'
   */
  fadeSegmentHeight?: number | string

  /**
   * The scroll range to use for the scroll fade effect.
   * The first number represents at what point to start the fade
   * at opacity 0, the second number is at which relative scroll offset
   * the opacity of the fade segment is 1.
   *
   * @default [0, 10]
   */
  inputScrollRange?: [number, number]
}

export interface UseScrollFadeMaskImageResult {
  /**
   * The `mask-image` style to apply to the scroll container.
   * The scroll container must be a Motion component.
   */
  maskImage: MotionValue<string>
}

/**
 * Applies a scroll fade effect to the scrollable container.
 */
export function useScrollFadeMaskImage({
  scrollYStart,
  scrollYEnd,
  fade,
  inputScrollRange = [0, 10],
  fadeSegmentHeight = 'var(--spacing-4xl)',
}: UseScrollFadeMaskImageProps): UseScrollFadeMaskImageResult {
  const fadeStartOpacity = useTransform(
    scrollYStart,
    inputScrollRange,
    fade === 'top' || fade === 'both' ? [0, 100] : [0, 0],
  )
  const fadeEndOpacity = useTransform(
    scrollYEnd,
    inputScrollRange,
    fade === 'bottom' || fade === 'both' ? [0, 100] : [0, 0],
  )

  fadeSegmentHeight =
    typeof fadeSegmentHeight === 'number'
      ? `${fadeSegmentHeight}px`
      : fadeSegmentHeight

  const maskImage = useMotionTemplate`
    linear-gradient(
      to bottom,
      color-mix(in srgb, white, transparent ${fadeStartOpacity}%) 0%,
      white ${fadeSegmentHeight},
      white calc(100% - ${fadeSegmentHeight}),
      color-mix(in srgb, white, transparent ${fadeEndOpacity}%)
    )
  `

  return {
    maskImage,
  }
}
